import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import {
  useGetInsurersPublicQuery,
  useLazyGetDefaultFormsQuery,
  useLazyGetInsurerDetailsQuery,
} from '@redux/queries/insurers'
import { Form } from '@typesApp/DynamicComponent'
import { DefaultFormArgs, Insurer, InsurerDetailArgs, InsurerDetails, InsurerFormsType } from '@typesApp/Insurer'
import { setForms } from '@redux/slices/formsSlice'
import { authTokenReadySelector, loggingOutSelector, sessionExpiredSelector } from '@redux/slices/connectorSlice'

type InsurerConfig = {
  insurer: Insurer
  forms: Form[]
}

type PromiseContext = {
  resolve: (insurer: InsurerConfig) => void
  reject: (error?: unknown) => void
}

type UseInsurerConfigurationResult = {
  insurers?: Insurer[]
  isLoading: boolean
  isError: boolean
  fetchInsurerConfiguration: (insurerId: string) => Promise<InsurerConfig | void>
}

const PREFER_CACHE_VALUE = true

export const useInsurerConfiguration = (skip?: boolean): UseInsurerConfigurationResult => {
  const dispatch = useDispatch()
  const isLoggingOut = useSelector(loggingOutSelector)
  const isTokenReady = useSelector(authTokenReadySelector)
  const isSessionExpired = useSelector(sessionExpiredSelector)
  const {
    data: insurers,
    isLoading: isLoadingInsurers,
    isError: isErrorLoadingInsurers,
  } = useGetInsurersPublicQuery(!skip && !isLoggingOut && !isSessionExpired ? undefined : skipToken)
  const [fetchDetails, { isLoading: isLoadingDetails, isError: isErrorLoadingDetails }] =
    useLazyGetInsurerDetailsQuery()
  const [fetchDefaultForms, { isLoading: isLoadingForm, isError: isErrorLoadingForm }] = useLazyGetDefaultFormsQuery()

  const fetchInsurerConfiguration = useCallback(
    (insurerId: string): Promise<InsurerConfig | void> => {
      // FIXME: until insurer details API is using apikey
      if (!isTokenReady) {
        return Promise.resolve()
      }

      const insurer = insurers?.find(i => i.id === insurerId)
      if (!insurer) {
        return Promise.resolve()
      }

      const initInsurerForms = (insurer: Insurer, forms: Form[], context: PromiseContext) => {
        dispatch(setForms(forms))
        context.resolve({ insurer, forms })
      }

      const fallbackToDefaultForms = (insurer: Insurer, context: PromiseContext) => {
        const args = { insuranceProviderId: insurer.insuranceProviderId } as DefaultFormArgs

        fetchDefaultForms(args, PREFER_CACHE_VALUE)
          .unwrap()
          .then(forms => {
            initInsurerForms(insurer, forms, context)
          })
      }

      const initCustomForms = (insurer: Insurer): Promise<InsurerConfig> => {
        const args = { insurerId: insurer.id } as InsurerDetailArgs

        return new Promise<InsurerConfig>((resolve, reject) => {
          fetchDetails(args, PREFER_CACHE_VALUE)
            .unwrap()
            .then(details => {
              if (hasCustomForms(details)) {
                initInsurerForms(insurer, details.dynamicFormsConfig?.formGroups, { resolve, reject })
              } else {
                fallbackToDefaultForms(insurer, { resolve, reject })
              }
            })
            .catch(error => {
              console.log(error)
              fallbackToDefaultForms(insurer, { resolve, reject })
            })
        })
      }

      const initDefaultForms = (insurer: Insurer) => {
        const args = { insuranceProviderId: insurer.insuranceProviderId } as DefaultFormArgs

        return new Promise<InsurerConfig>((resolve, reject) => {
          fetchDefaultForms(args, PREFER_CACHE_VALUE)
            .unwrap()
            .then(forms => {
              initInsurerForms(insurer, forms, { resolve, reject })
            })
        })
      }

      if (insurer.dynamicFormsType === InsurerFormsType.CUSTOM) {
        return initCustomForms(insurer)
      } else {
        return initDefaultForms(insurer)
      }
    },
    [dispatch, fetchDetails, fetchDefaultForms, insurers, isTokenReady]
  )

  return {
    isLoading: isLoadingInsurers || isLoadingDetails || isLoadingForm,
    isError: isErrorLoadingInsurers || isErrorLoadingDetails || isErrorLoadingForm,
    insurers,
    fetchInsurerConfiguration,
  }
}

const hasCustomForms = (details: InsurerDetails): boolean => {
  return Array.isArray(details.dynamicFormsConfig?.formGroups) && details.dynamicFormsConfig.formGroups.length > 0
}
